import React, { useRef, useState, useEffect } from "react";
import flvjs from "flv.js";
import Hls from "hls.js";
import {
  PlayCircleOutline as PlayIcon,
  MoreVert as MenuIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { Menu, MenuItem, IconButton } from "@mui/material";

const useStyles = makeStyles((theme) => ({
  player: {
    maxWidth: "100%",
    width: "100%",
  },
  root: {
    margin: 0,
    position: "relative",
  },
  caption: {
    top: 0,
    position: "absolute",
    left: 0,
    right: 0,
    textAlign: "center",
    background:
      "linear-gradient(to bottom, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0) 100%)",
    paddingBottom: theme.spacing(2),
    color: "#fff",
    display: "flex",
    alignItems: "center",
  },
  cationLabel: {
    flexGrow: 1,
  },
  playIconOverlay: {
    display: "grid",
    placeItems: "center",
    top: "30%",
    bottom: "30%",
    left: "35%",
    right: "35%",
    position: "absolute",
    textAlign: "center",
    background: "rgba(0,0,0,0.85)",
    borderRadius: theme.spacing(2),
    cursor: "pointer",
    zIndex: 1,
    color: "#fff",
    "&:hover": {
      background: `radial-gradient(#2e2e2e, rgba(0,0,0,0.85))`,
    },
  },
  playIcon: {
    width: "90%",
    height: "90%",
  },
  menuLeft: {
    width: "34px",
  },
}));

const rtmpPlayerSetup = (video, src, onStopClick) => {
  let flvPlayer = flvjs.createPlayer(
    {
      type: "flv",
      url: src,
    },
    {
      isLive: true,
      enableStashBuffer: false,
    },
  );

  flvPlayer.attachMediaElement(video);
  flvPlayer.load();
  flvPlayer.play();

  const destroyPlayer = (player) => {
    player.pause();
    player.unload();
    player.detachMediaElement();
    player.destroy();
  };

  const volumnchangeEventListener = () => {
    flvPlayer.muted = true;
  };

  video.addEventListener("pause", onStopClick);
  video.addEventListener("volumechange", volumnchangeEventListener);

  return () => {
    video.removeEventListener("pause", onStopClick);
    video.removeEventListener("volumechange", volumnchangeEventListener);
    destroyPlayer(flvPlayer);
  };
};

const hlsPlayerSetup = (video, src, onStopClick, onError) => {
  let hls = new Hls({
    enableWorker: true,
    autoStartLoad: true,
  });

  let isDestroyed = false;

  hls.attachMedia(video);

  hls.on(Hls.Events.MEDIA_ATTACHED, () => {
    if (!isDestroyed) hls.loadSource(src);
  });

  hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
    console.log(
      "Manifest loaded, found " + data.levels.length + " quality level",
    );
    if (!isDestroyed) {
      video.play().catch((e) => {
        if (e.name !== "AbortError") {
          console.warn("Autoplay prevented:", e);
        }
      });
    }
  });

  hls.on(Hls.Events.ERROR, (event, data) => {
    console.error("HLS Error:", {
      type: data.type,
      details: data.details,
      fatal: data.fatal,
    });

    if (data.fatal) {
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          if (
            data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
            data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT
          ) {
            onError?.("Connection lost");
            setTimeout(() => {
              if (!isDestroyed) hls.loadSource(src);
            }, 2000);
          } else {
            hls.startLoad();
          }
          break;

        case Hls.ErrorTypes.MEDIA_ERROR:
          hls.recoverMediaError();
          break;

        default:
          onError?.("fatal error encountered, can not recover");
          hls.destroy();
          isDestroyed = true;
          break;
      }
    }
  });

  hls.startLoad();

  video.addEventListener("stalled", () => {
    if (!isDestroyed) hls.startLoad();
  });

  const volumnchangeEventListener = () => {
    video.muted = true;
  };

  video.addEventListener("pause", onStopClick);
  video.addEventListener("volumechange", volumnchangeEventListener);

  return () => {
    isDestroyed = true;
    video.removeEventListener("pause", onStopClick);
    video.removeEventListener("volumechange", volumnchangeEventListener);
    if (hls) {
      hls.detachMedia();
      hls.destroy();
      hls = null;
    }
  };
};

const LiveVideoHtml5Player = ({
  label,
  sources,
  onSetCameraLocationClick,
  onPlayClick,
  onStopClick,
  play,
}) => {
  const classes = useStyles();
  const videoRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
  }, [sources]);

  useEffect(() => {
    const video = videoRef.current;
    if (play && video) {
      setError(null);

      const hlsSupported = Hls.isSupported();
      const hlsNativeSupported = video.canPlayType(
        "application/vnd.apple.mpegurl",
      );
      const flvSupported = flvjs.isSupported();

      if (flvSupported) {
        const src = sources.find((x) => x.type === "rtmp")?.source;
        if (src) return rtmpPlayerSetup(video, src, onStopClick);
      } else if (hlsNativeSupported) {
        const src = sources.find((x) => x.type === "hls")?.source;
        if (src) {
          video.src = src;
          video.play().catch((e) => {
            if (e.name !== "AbortError") {
              console.error("Native HLS play error:", e);
              setError("Unable to play video");
            }
          });
        }
      } else if (hlsSupported) {
        const src = sources.find((x) => x.type === "hls")?.source;
        if (src) {
          return hlsPlayerSetup(video, src, onStopClick, setError);
        }
      }
    }
  }, [play, sources, onStopClick]);

  const handleRetry = () => {
    setError(null);
  };

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const handleMenuButtonClick = (event) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);
  const handleCameraLocationClick = () => {
    setMenuAnchorEl(null);
    onSetCameraLocationClick();
  };

  return (
    <figure className={classes.root}>
      {!play ? (
        <>
          <div className={classes.playIconOverlay} onClick={onPlayClick}>
            <PlayIcon className={classes.playIcon} />
          </div>
          <video poster="/live-video-poster.svg"></video>
        </>
      ) : (
        <>
          {error ? (
            <>
              <div className={classes.playIconOverlay} onClick={handleRetry}>
                <div>
                  <ErrorIcon style={{ fontSize: 64, marginBottom: 8 }} />
                  <div>Retry</div>
                </div>
              </div>
              <video poster="/live-video-poster.svg"></video>
            </>
          ) : (
            <video
              className={classes.player}
              ref={videoRef}
              playsInline
              poster="/live-video-poster.svg"
              controls
              muted
              autoPlay={false}
            >
              Sorry, your browser doesn't support embedded videos.
            </video>
          )}
        </>
      )}

      <figcaption className={classes.caption}>
        <div className={classes.menuLeft}></div>
        <div className={classes.cationLabel}>{label}</div>
        <div>
          <IconButton
            color="inherit"
            aria-controls={menuOpen ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            onClick={handleMenuButtonClick}
            size="small"
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem onClick={handleCameraLocationClick}>
              Rename camera
            </MenuItem>
          </Menu>
        </div>
      </figcaption>
    </figure>
  );
};

export default LiveVideoHtml5Player;
